import type { TFunction } from 'i18next';
import type { SurveyForm } from './types';
import { getLangName, getLangRequirement } from './data';

export function validateStepInfo(form: SurveyForm, t: TFunction): string[] {
  const errors: string[] = [];
  if (!form.name.trim())            errors.push(t('admin.builderValidationName'));
  if (!form.surveyType)             errors.push(t('admin.builderValidationSurveyType'));
  if (!form.startDate)              errors.push(t('admin.builderValidationStartDate'));
  if (!form.expirationDate)         errors.push(t('admin.builderValidationEndDate'));
  if (form.startDate && form.expirationDate && form.expirationDate < form.startDate)
    errors.push(t('admin.builderValidationDateRange'));

  const todayStr = new Date().toISOString().split('T')[0];
  if (form.startDate && form.startDate < todayStr)
    errors.push(t('admin.builderValidationStartDatePast'));
  if (form.expirationDate && form.expirationDate < todayStr)
    errors.push(t('admin.builderValidationEndDatePast'));

  if (form.activeDays.length === 0) errors.push(t('admin.builderValidationActiveDays'));
  return errors;
}

export function validateStepAudiencia(form: SurveyForm, t: TFunction): string[] {
  const errors: string[] = [];
  if (form.surveyType === 'personalizada') {
    if (form.destMode === 'meeting' && !form.sessionId.trim())
      errors.push(t('admin.builderValidationMeetingId'));
    if (form.destMode === 'people' && form.destPeople.length === 0)
      errors.push(t('admin.builderValidationPeople'));
    if (form.destMode === 'ou' && !form.destOU)
      errors.push(t('admin.builderValidationOU'));
  }
  return errors;
}

export function validateStepPreguntas(form: SurveyForm, t: TFunction): string[] {
  if (form.questions.length === 0) {
    return [t('admin.builderValidationNoQuestions')];
  }
  return [];
}

export function validateStepConfig(form: SurveyForm, t: TFunction, gdprOuKey: string | undefined): string[] {
  const errors: string[] = [];
  if (!form.minRespondents || form.minRespondents < 1)
    errors.push(t('admin.builderValidationMinParticipants'));
  if (!form.maxRespondents || form.maxRespondents < 1)
    errors.push(t('admin.builderValidationMaxParticipants'));
  if (form.minRespondents > 0 && form.maxRespondents > 0 && form.minRespondents > form.maxRespondents)
    errors.push(t('admin.builderValidationParticipantsRange'));

  const { mandatory: reqLangs } = getLangRequirement(form, gdprOuKey);
  reqLangs.forEach(lang => {
    if (form.translationsEnabled[lang] === false) return;
    const reqKeys = getRequiredTranslationKeys(form, lang);
    const allFilled = reqKeys.every(k => form.translations?.[lang]?.[k]?.trim());
    if (!allFilled) errors.push(`${t('admin.builderValidationTranslations')}: ${getLangName(lang, t)}`);
  });
  return errors;
}

/** Claves de traducción exigidas para un idioma dado — excluye las preguntas cuyo `sourceLang`
 *  ya coincide con ese idioma, igual que `TranslationsSection.getRequiredTrlKeys`, para no pedir
 *  traducción de una pregunta a su propio idioma origen. */
function getRequiredTranslationKeys(form: SurveyForm, lang: string): string[] {
  const keys: string[] = ['survey_name'];
  form.questions.forEach((q, qi) => {
    if (q.sourceLang === lang) return;
    keys.push(`q_${qi}`);
    const hasOpts = q.type === 'multiple' || (q.type === 'behaviors' && q.enfoque === 'individual');
    if (hasOpts) q.options.forEach((_, oi) => keys.push(`q_${qi}_opt_${oi}`));
  });
  return keys;
}
